import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import { baseurl } from "../baseurl";
import { FiTrash2, FiMail, FiUser, FiCalendar, FiExternalLink, FiSearch } from "react-icons/fi";
import Swal from "sweetalert2";

function AllContactForm() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getAllContactSubmissions`);
      if (res.data.success) setContacts(res.data.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, []);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete message?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${baseurl}deleteContactSubmission/${id}`);
        if (res.data.success) {
          setContacts(contacts.filter((item) => item.id !== id));
          Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Removed', showConfirmButton: false, timer: 1500 });
        }
      } catch (err) { Swal.fire("Error", "Action failed", "error"); }
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    c.subject.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3">
          {/* Header & Quick Stats */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <h5 className="fw-bold mb-0">Inquiry Inbox</h5>
              <p className="extra-small text-muted mb-0">Manage customer messages and support tickets</p>
            </div>
            <div className="d-flex gap-2">
              <div className="stat-badge bg-white shadow-sm border px-3 py-1 rounded-pill">
                <span className="text-primary fw-bold">{contacts.length}</span> <small className="text-muted">Total</small>
              </div>
              <div className="stat-badge bg-white shadow-sm border px-3 py-1 rounded-pill">
                <span className="text-success fw-bold">{contacts.filter(c => new Date(c.created_at).toDateString() === new Date().toDateString()).length}</span> <small className="text-muted">New Today</small>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm overflow-hidden">
            {/* Toolbar */}
            <div className="card-header bg-white border-0 py-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-0"><FiSearch className="text-muted"/></span>
                    <input 
                      type="text" 
                      className="form-control form-control-sm border-0 bg-light" 
                      placeholder="Search sender or subject..." 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-8 text-end">
                   <button className="btn btn-sm btn-outline-secondary border-0" onClick={fetchContacts}>Refresh List</button>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 compact-table">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-3" style={{width: '25%'}}>Sender</th>
                    <th style={{width: '25%'}}>Subject</th>
                    <th>Date</th>
                    <th className="text-center">Message</th>
                    <th className="text-end pe-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : filteredContacts.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">No submissions found.</td></tr>
                  ) : (
                    filteredContacts.map((contact) => (
                      <tr key={contact.id}>
                        <td className="ps-3">
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm bg-soft-primary text-primary me-2">
                              {contact.name.charAt(0)}
                            </div>
                            <div>
                              <div className="fw-bold small">{contact.name}</div>
                              <div className="extra-small text-muted">{contact.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small text-dark fw-semibold text-truncate" style={{maxWidth: '180px'}}>
                            {contact.subject}
                          </div>
                        </td>
                        <td>
                          <div className="extra-small text-muted">
                            <FiCalendar className="me-1"/>{new Date(contact.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="text-center">
                          <button 
                            className="btn btn-xs btn-light text-primary border rounded-pill px-3"
                            onClick={() => Swal.fire({
                              title: `<span class="small">${contact.subject}</span>`,
                              html: `<div class="text-start p-2"><p class="text-muted small">From: ${contact.email}</p><hr/><p>${contact.message}</p></div>`,
                              icon: "info",
                              confirmButtonColor: "#4f46e5"
                            })}
                          >
                            <FiExternalLink size={12} className="me-1"/> View
                          </button>
                        </td>
                        <td className="text-end pe-3">
                          <button
                            className="btn btn-sm text-danger btn-link p-0 hover-bg-danger"
                            onClick={() => handleDelete(contact.id)}
                            title="Delete Permanently"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .compact-table thead th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; padding: 12px 8px; border-top: none; }
        .compact-table tbody td { padding: 10px 8px; border-bottom: 1px solid #f1f5f9; }
        .avatar-sm { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13px; }
        .bg-soft-primary { background-color: #eef2ff; }
        .btn-xs { padding: 2px 8px; font-size: 11px; font-weight: 600; }
        .hover-bg-danger:hover { color: #dc2626 !important; }
        .stat-badge { font-size: 12px; }
      `}</style>
    </>
  );
}

export default AllContactForm;